/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();

      this.variant = this.getVariantFromSelectedOptions();
      if (this.variant) {
        this.updateMedia();
      }
      this.updateAvailability();
      this.updateSku();
      this.updateOptionCardContent();

      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = this.getVariantFromSelectedOptions();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateOptionCardContent();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }
    buildSizedImageUrl(src, width) {
      if (!src) return '';
    
      const safeWidth = parseInt(width, 10) || 80;
    
      if (src.includes('width=')) {
        return src.replace(/width=\d+/i, `width=${safeWidth}`);
      }
    
      return src.includes('?')
        ? `${src}&width=${safeWidth}`
        : `${src}?width=${safeWidth}`;
    }
    /**
     * Gets the selected variant from current selected options.
     * @returns {?object}
     */
    getVariantFromSelectedOptions() {
      const selectedOptions = this.getSelectedOptions();

      return this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      ) || null;
    }

    /**
     * Returns the best preview variant for a given option value
     * under the current selected context.
     * @param {number} optionIndex
     * @param {string} optionValue
     * @returns {?object}
     */
    getPreviewVariantForOption(optionIndex, optionValue) {
      const selectedOptions = this.getSelectedOptions();

      const candidates = this.data.product.variants.filter(
        (variant) => variant.options[optionIndex] === optionValue
      );

      if (!candidates.length) return null;

      let bestVariant = null;
      let bestScore = -1;

      candidates.forEach((variant) => {
        let score = 0;

        variant.options.forEach((val, idx) => {
          if (idx !== optionIndex && val === selectedOptions[idx]) {
            score += 1;
          }
        });

        if (!bestVariant) {
          bestVariant = variant;
          bestScore = score;
          return;
        }

        const bestAvailable = !!bestVariant.available;
        const currentAvailable = !!variant.available;

        if (currentAvailable && !bestAvailable) {
          bestVariant = variant;
          bestScore = score;
          return;
        }

        if (currentAvailable === bestAvailable && score > bestScore) {
          bestVariant = variant;
          bestScore = score;
        }
      });

      return bestVariant;
    }

    /**
     * Updates non-color option cards image/sku/price
     * based on current selected option context.
     */
    updateOptionCardContent() {
      const selectors = this.querySelectorAll('.option-selector[data-is-color="false"]');

      selectors.forEach((selector) => {
        const optionIndex = Number(selector.dataset.optionIndex);
        const optionCards = selector.querySelectorAll('[data-option-card]');

        optionCards.forEach((card) => {
          const optionValue = card.dataset.optionValue;
          const previewVariant = this.getPreviewVariantForOption(optionIndex, optionValue);

          const mediaWrap = card.querySelector('.opt-card__media');
          const imageEl = card.querySelector('.js-option-card-image');
          const skuEl = card.querySelector('.js-option-card-sku');
          const currentPriceEl = card.querySelector('.js-option-card-price');
          const originalPriceEl = card.querySelector('.js-option-card-compare');

          if (!previewVariant) {
            if (mediaWrap) mediaWrap.hidden = true;
            if (imageEl) {
              imageEl.removeAttribute('src');
              imageEl.hidden = true;
            }
            if (skuEl) {
              skuEl.textContent = '';
              skuEl.hidden = true;
            }
            if (currentPriceEl) {
              currentPriceEl.textContent = '';
              currentPriceEl.hidden = true;
            }
            if (originalPriceEl) {
              originalPriceEl.textContent = '';
              originalPriceEl.hidden = true;
            }
            return;
          }

          const imageUrl =
            previewVariant.featured_media?.preview_image?.src ||
            previewVariant.featured_media?.src ||
            previewVariant.featured_image?.src ||
            '';

          if (imageUrl && imageEl) {
            const sizedImageUrl = this.buildSizedImageUrl(imageUrl, 90);
            imageEl.src = sizedImageUrl;
            imageEl.alt = optionValue;
            imageEl.hidden = false;
            if (mediaWrap) mediaWrap.hidden = false;
          } else {
            if (mediaWrap) mediaWrap.hidden = true;
            if (imageEl) {
              imageEl.removeAttribute('src');
              imageEl.hidden = true;
            }
          }

          if (skuEl) {
            if (previewVariant.sku) {
              skuEl.textContent = previewVariant.sku;
              skuEl.hidden = false;
            } else {
              skuEl.textContent = '';
              skuEl.hidden = true;
            }
          }

          const formattedVariant = this.data.formatted[String(previewVariant.id)] || {};
          const currentPrice = formattedVariant.cardCurrentPrice || '';
          const originalPrice = formattedVariant.cardOriginalPrice || '';

          if (currentPriceEl) {
            if (currentPrice) {
              currentPriceEl.textContent = currentPrice;
              currentPriceEl.hidden = false;
            } else {
              currentPriceEl.textContent = '';
              currentPriceEl.hidden = true;
            }
          }

          if (originalPriceEl) {
            if (originalPrice) {
              originalPriceEl.textContent = originalPrice;
              originalPriceEl.hidden = false;
            } else {
              originalPriceEl.textContent = '';
              originalPriceEl.hidden = true;
            }
          }
        });
      });
    }
    
    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.getSelectedOptions() };
      }

      const updateOptionAvailability = (optionEl, available, soldout) => {
        const el = optionEl;
        const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
        el.classList.toggle('is-unavailable', !available);

        const nextLabel = el.nextElementSibling;
        if (nextLabel) {
          nextLabel.classList.toggle('is-unavailable', !available);
        }

        if (optionEl.classList.contains('custom-select__option')) {
          const em = el.querySelector('em');

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = text;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
            }
          }
        } else if (!available) {
          if (nextLabel) nextLabel.title = text;
        } else {
          if (nextLabel) nextLabel.removeAttribute('title');
        }
      };

      // Flag all options as unavailable
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        updateOptionAvailability(optionEl, false, false);
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll('.js-option');
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === 'dropdown') {
                return opt.dataset.optionValue === variant.options[selectorIndex];
              }
              const nextLabel = opt.nextElementSibling;
              const valueElement = nextLabel?.querySelector('.js-value');
              return valueElement?.dataset.optionValue === variant.options[selectorIndex];
            });

            if (optionEl) {
              updateOptionAvailability(optionEl, variant.available, !variant.available);
            }
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formattedVariant = this.data.formatted[this.variant.id];

        if (formattedVariant) {
          const { inventory } = formattedVariant;

          if (this.variant.inventory_management && inventory === 'none') {
            const backorderProdEl = this.backorder.querySelector('.backorder__product');
            const prodTitleEl = this.section.querySelector('.product-title');
            const variantTitle = this.variant.title.includes('Default')
              ? ''
              : ` - ${this.variant.title}`;

            if (backorderProdEl && prodTitleEl) {
              backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
              hideBackorder = false;
            }
          }
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling?.querySelector('.js-value');
      if (!valueEl) return;

      colorText.textContent = valueEl.textContent;
    }

    /**
     * Updates the product media.
     * 需求：
     * 1. 变体图仍存在于 gallery 中，但切换 variant 时不跳过去
     * 2. 只替换第一张主图的图片内容
     */
    updateMedia() {
      if (!this.variant || !this.variant.featured_media) return;
    
      if (this.section.matches('quick-add-drawer')) {
        if (typeof this.section.updateMedia === 'function') {
          this.section.updateMedia(this.variant.featured_media.id);
        }
        return;
      }
    
      this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
      if (!this.mediaGallery) return;
    
      if (typeof this.mediaGallery.updateMainMediaImage === 'function') {
        this.mediaGallery.updateMainMediaImage(this.variant);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price .price');

      if (!this.price || !this.variant || !this.variant.id) return;

      const requestedVariantId = this.variant.id;
      this._priceReqId = (this._priceReqId || 0) + 1;
      const reqId = this._priceReqId;
      const productHandle = this.dataset.productHandle;
      if (!productHandle) return;

      const url = `/products/${productHandle}?variant=${this.variant.id}&section_id=main-product`;

      fetch(url)
        .then((res) => res.text())
        .then((html) => {
          if (!this.variant || this.variant.id !== requestedVariantId) return;
          if (reqId !== this._priceReqId) return;

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          const newPrice = tempDiv.querySelector('.product-info__price .price');
          if (newPrice) {
            this.price.replaceWith(newPrice);
            this.price = newPrice;
          }

          const currentEl = newPrice?.querySelector('.price__current');
          const currentText = currentEl ? currentEl.textContent.trim() : '';

          this.dispatchEvent(new CustomEvent('on:variant:price:updated', {
            bubbles: true,
            detail: {
              variantId: requestedVariantId,
              priceCurrentText: currentText
            }
          }));
        })
        .catch(() => { });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false' || !this.variant) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      if (!this.variant) return;

      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          selectedOptions.push(btn ? btn.textContent.trim() : '');
        } else {
          const checked = selector.querySelector('input:checked');
          selectedOptions.push(checked ? checked.value : '');
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }
  }

  customElements.define('variant-picker', VariantPicker);
}